/**
 * Decided economics (docs/ECONOMICS.md, 2026-09-24). Shared by api, runtime
 * and web; the contracts carry their own copies (deploy parameters).
 */

/** Default execution fee a mandate carries, in bps of executed notional. */
export const DEFAULT_EXECUTION_FEE_BPS = 10
/** Maximum execution fee a creator may set (`MAX_FEE_BPS`). */
export const MAX_EXECUTION_FEE_BPS = 50
/** Default split of execution and subscription fees: holders 40 %, creator 40 %, platform 20 %. */
export const DEFAULT_FEE_SPLIT = { holderBps: 0, creatorBps: 8000, platformBps: 2000 } as const
/** Performance fee cap: 10 % of realized profit above the high-water mark. */
export const MAX_PERFORMANCE_FEE_BPS = 1000
/** "Unlimited" per-token cap: 2^256 - 1 (the owner decided no platform-imposed caps). */
export const MAX_UINT256 = (1n << 256n) - 1n
export const UNLIMITED_TOKEN_CAP = MAX_UINT256.toString()
/** 1 credit = USD 0.01 = 1 000 000 micro-credits. */
export const MICRO_PER_CREDIT = 1_000_000n

/**
 * FeeDistributor production parameters (docs/CONTRACTS.md "FeeDistributor", docs/ECONOMICS.md "Holder earnings").
 * The distributor runs on the Ethereum L1 block clock (`block.number` on Robinhood Chain): an epoch lasts
 * 50 400 L1 blocks (about 7 days at 12 s), the holding rule looks back 7 200 L1 blocks (about one day), and an
 * epoch's unclaimed remainder rolls forward after 52 later epochs (about a year). A deployment may differ (local
 * chains use short epochs): reads prefer the deployment's own values and fall back to these.
 */
export const DISTRIBUTOR_EPOCH_LENGTH_BLOCKS = 50_400
export const DISTRIBUTOR_HOLD_BLOCKS = 7_200
export const DISTRIBUTOR_CLAIM_WINDOW_EPOCHS = 52
/** Seconds per Ethereum L1 block (the distributor clock). */
export const L1_BLOCK_SECONDS = 12
/** The production distributor parameters in one object (pricing, docs and earnings copy). */
export const DISTRIBUTOR = {
  epochLengthBlocks: DISTRIBUTOR_EPOCH_LENGTH_BLOCKS,
  holdBlocks: DISTRIBUTOR_HOLD_BLOCKS,
  claimWindowEpochs: DISTRIBUTOR_CLAIM_WINDOW_EPOCHS,
  blockSeconds: L1_BLOCK_SECONDS,
} as const
